import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NowPlaying from './NowPlaying';
import './Header.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null); // 'success' | 'error' | 'token-expired' | null
  const [syncError, setSyncError] = useState('');
  const [authUrl, setAuthUrl] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear sync status after a delay
  useEffect(() => {
    if (syncStatus === 'success') {
      const timer = setTimeout(() => setSyncStatus(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [syncStatus]);

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncStatus(null);
    setSyncError('');
    setAuthUrl(null);
    
    try {
      const res = await fetch(`${API_BASE}/admin/trigger-sync`, { 
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      
      if (res.ok && data.message) {
        // Sync started - poll for completion
        pollSyncStatus();
      } else if (data.needsAuth && data.authUrl) {
        // Token needs re-auth
        setSyncStatus('token-expired');
        setSyncError(data.error || 'Spotify token expired');
        setAuthUrl(data.authUrl);
        setSyncing(false);
      } else if (data.error?.includes('token') || data.error?.includes('expired')) {
        setSyncStatus('token-expired');
        setSyncError('Spotify token expired');
        setAuthUrl(`${API_BASE.replace('/api', '')}/auth/spotify`);
        setSyncing(false);
      } else {
        setSyncStatus('error');
        setSyncError(data.error || 'Sync failed');
        setSyncing(false);
      }
    } catch (err) {
      setSyncStatus('error');
      setSyncError('Network error');
      setSyncing(false);
    }
  };

  const pollSyncStatus = async () => {
    // Poll the health endpoint to check if sync completed
    let attempts = 0;
    const maxAttempts = 30; // 30 * 2s = 60s max
    
    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_BASE.replace('/api', '')}/health`);
        const data = await res.json();
        
        // Check if recent log shows completion
        const recentLogs = data.recentSyncLogs || [];
        const hasCompleted = recentLogs.some(log => 
          log.includes('Sync completed successfully') || 
          log.includes('completed')
        );
        const hasError = recentLogs.some(log => 
          log.includes('error') || 
          log.includes('failed') ||
          log.includes('401') ||
          log.includes('expired')
        );
        
        if (hasCompleted && !syncing) {
          // Already marked as done
          return;
        }
        
        if (hasCompleted) {
          setSyncStatus('success');
          setSyncing(false);
          return;
        }
        
        if (hasError) {
          const errorLog = recentLogs.find(log => log.includes('error') || log.includes('401'));
          if (errorLog?.includes('401') || errorLog?.includes('expired')) {
            setSyncStatus('token-expired');
            setSyncError('Spotify token expired');
          } else {
            setSyncStatus('error');
            setSyncError('Sync failed - check logs');
          }
          setSyncing(false);
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 2000);
        } else {
          // Timeout - assume it worked
          setSyncStatus('success');
          setSyncing(false);
        }
      } catch (err) {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 2000);
        } else {
          setSyncStatus('error');
          setSyncError('Status check failed');
          setSyncing(false);
        }
      }
    };
    
    // Start polling after 2 seconds
    setTimeout(checkStatus, 2000);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  
  // Determine which nav item is active based on current path
  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Get sync indicator class
  const getSyncIndicatorClass = () => {
    if (syncing) return 'sync-indicator syncing';
    if (syncStatus === 'success') return 'sync-indicator success';
    if (syncStatus === 'error' || syncStatus === 'token-expired') return 'sync-indicator error';
    return 'sync-indicator';
  };
  
  return (
    <header className="site-header">
      <div className="header-container">
        <Link to="/" className="site-logo">
          Matt's Rides
        </Link>
        
        <nav className="site-nav">
          <Link 
            to="/" 
            className={`nav-link ${isActive('/') && location.pathname === '/' ? 'active' : ''}`}
          >
            Recent
          </Link>
          <Link 
            to="/top-songs" 
            className={`nav-link ${isActive('/top-songs') ? 'active' : ''}`}
          >
            Top Songs
          </Link>
          <Link 
            to="/top-artists" 
            className={`nav-link ${isActive('/top-artists') ? 'active' : ''}`}
          >
            Top Artists
          </Link>
          <Link 
            to="/search" 
            className={`nav-link ${isActive('/search') ? 'active' : ''}`}
          >
            Search
          </Link>
          <Link
            to="/stats"
            className={`nav-link ${isActive('/stats') ? 'active' : ''}`}
          >
            Stats
          </Link>
          {/* Services link removed - functionality moved to Mission Control */}
        </nav>
        
        <div className="header-actions">
          {isAuthenticated && (
            <>
              {/* Now Playing Widget */}
              <NowPlaying />

              {/* Sync Status Indicator */}
              <span className={getSyncIndicatorClass()} title={
                syncing ? 'Syncing...' :
                syncStatus === 'success' ? 'Sync complete!' :
                syncStatus === 'error' ? syncError :
                syncStatus === 'token-expired' ? 'Token expired' : ''
              }></span>

              <div className="user-dropdown" ref={dropdownRef}>
                <button 
                  className="user-dropdown-trigger"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  <span className="user-email">{user?.email?.split('@')[0]}</span>
                  <span className="dropdown-arrow">{dropdownOpen ? '▲' : '▼'}</span>
                </button>
                {dropdownOpen && (
                  <div className="dropdown-menu">
                    <button 
                      className={`dropdown-item ${syncing ? 'syncing' : ''}`}
                      onClick={() => { handleSync(); }}
                      disabled={syncing}
                    >
                      <span className={`sync-icon ${syncing ? 'spinning' : ''}`}>↻</span>
                      {syncing ? 'Syncing...' : 'Sync'}
                    </button>
                    
                    {syncStatus === 'token-expired' && authUrl && (
                      <a 
                        href={authUrl}
                        className="dropdown-item token-refresh"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setDropdownOpen(false)}
                      >
                        🔑 Re-authenticate Spotify
                      </a>
                    )}
                    
                    {syncStatus === 'error' && (
                      <div className="dropdown-item error-msg">
                        ⚠️ {syncError}
                      </div>
                    )}
                    
                    <Link 
                      to="/builder" 
                      className="dropdown-item"
                      onClick={() => setDropdownOpen(false)}
                    >
                      🎛️ Builder v3
                    </Link>
                    <Link 
                      to="/library" 
                      className="dropdown-item"
                      onClick={() => setDropdownOpen(false)}
                    >
                      📚 Library
                    </Link>
                    <Link
                      to="/generate"
                      className="dropdown-item"
                      onClick={() => setDropdownOpen(false)}
                    >
                      🎲 Generator
                    </Link>
                    <Link
                      to="/admin/ratings"
                      className="dropdown-item"
                      onClick={() => setDropdownOpen(false)}
                    >
                      ⭐ Ratings
                    </Link>
                    <div className="dropdown-divider"></div>
                    <button 
                      className="dropdown-item logout"
                      onClick={handleLogout}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
          {!isAuthenticated && (
            <Link to="/login" className="login-link">
              Login
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
